import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactElement } from 'react';
import { MemoryRouter } from 'react-router-dom';

import { SessionController, type PersistedSession, type SessionControllerDeps, type SessionStore } from '../SessionController';
import type { StorageLike } from '../rounds/rounds-credential-store';
import { clearMatchSession } from '../../session/match-session';
import { usePresentationPalette } from './usePresentationPalette';

type Team = 'red' | 'blue';

interface PresentationStatus {
  phase: string;
  generation: number;
  matchId: string | null;
  red?: { name: string; claimed: boolean; connected: boolean };
  blue?: { name: string; claimed: boolean; connected: boolean };
  palette?: { red: string; blue: string };
}

interface ClaimResponse {
  generation: number;
  team: Team;
  session: { token: string; matchId: string };
}

interface StoredClaim {
  generation: number;
  token: string;
  matchId: string;
}

export interface PresentationPlayerRootProps {
  team: Team;
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  storage?: StorageLike;
  sessionStore?: SessionStore;
  transportFactory: SessionControllerDeps['transportFactory'];
  scheduler?: SessionControllerDeps['scheduler'];
  rng?: SessionControllerDeps['rng'];
  createRenderer?: SessionControllerDeps['createRenderer'];
}

/**
 * **`localStorage`, not `sessionStorage`** (deathmatch rehearsal, 2026-08-12 — plan **D-4**).
 *
 * The seat credential *is* the seat identity: the server admits a reclaim by byte-equality against
 * the token it issued (`waggle-server/src/presentation.rs`), and nothing else identifies a player —
 * not Entra, which changes reachability rather than identity (`docs/dev/server-hosting.md`). Held
 * in `sessionStorage` the credential was per-tab and destroyed on close, so a player who shut their
 * browser came back with no token, claimed without one, and got 409 `seat_occupied` on a seat only
 * the presenter could release.
 *
 * Persisting it across sessions is safe because a token from a superseded match already degrades to
 * the reset path rather than to a new failure mode: the generation check here refuses to spend it,
 * and a server that disagrees answers 410 `match_reset`, which this component renders.
 */
function browserSeatStorage(): StorageLike {
  return {
    getItem: (key) => window.localStorage.getItem(key),
    setItem: (key, value) => window.localStorage.setItem(key, value),
    removeItem: (key) => window.localStorage.removeItem(key),
  };
}

function parseStored(storage: StorageLike, key: string): StoredClaim | null {
  try {
    const raw = storage.getItem(key);
    if (raw === null) return null;
    const value = JSON.parse(raw) as Partial<StoredClaim>;
    return typeof value.generation === 'number' && typeof value.token === 'string' &&
      typeof value.matchId === 'string' ? value as StoredClaim : null;
  } catch {
    return null;
  }
}

function presentationSessionStore(
  session: ClaimResponse['session'],
  delegate?: SessionStore,
): SessionStore {
  let record: PersistedSession | null = {
    token: session.token,
    matchId: session.matchId,
    role: 'player',
    phase: 'connecting',
    isHost: false,
  };
  return {
    get: () => record,
    set: (next) => {
      record = next;
      delegate?.set(next);
    },
    clear: () => {
      record = null;
      delegate?.clear();
    },
  };
}

export function PresentationPlayerRoot({
  team,
  baseUrl = '',
  fetchImpl = globalThis.fetch.bind(globalThis),
  storage,
  sessionStore,
  transportFactory,
  scheduler,
  rng,
  createRenderer,
}: PresentationPlayerRootProps): ReactElement {
  const claimStorage = useMemo(() => storage ?? browserSeatStorage(), [storage]);
  const key = `waggle-presentation-${team}`;
  const [claim, setClaim] = useState<ClaimResponse | null>(null);
  const [wasReset, setWasReset] = useState(false);
  const [message, setMessage] = useState(`Waiting for the presenter…`);
  const [attempt, setAttempt] = useState(0);
  const inFlight = useRef<Promise<ClaimResponse | null> | null>(null);
  const root = baseUrl.replace(/\/+$/, '');
  const [palette, setPalette] = useState<PresentationStatus['palette']>();

  usePresentationPalette(palette);

  useEffect(() => {
    let live = true;
    let retryTimer: number | undefined;
    const scheduleRetry = (): void => {
      if (!live) return;
      retryTimer = window.setTimeout(() => {
        inFlight.current = null;
        setAttempt((value) => value + 1);
      }, 1_000);
    };
    const run = async (): Promise<ClaimResponse | null> => {
      const response = await fetchImpl(`${root}/presentation/status`);
      if (!response.ok) throw new Error('status unavailable');
      const status = await response.json() as PresentationStatus;
      setPalette(status.palette);
      if (status.matchId === null || status.phase === 'waitingForPresentation') {
        setMessage('Waiting for the presenter…');
        return null;
      }
      const stored = parseStored(claimStorage, key);
      if (stored !== null && stored.generation !== status.generation) {
        setMessage('This match was reset. A new player can now take this seat.');
        return null;
      }
      const responseClaim = await fetchImpl(`${root}/presentation/seats/${team}/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          generation: status.generation,
          ...(stored === null ? {} : { token: stored.token }),
        }),
      });
      if (responseClaim.status === 409) {
        setMessage(`The ${team === 'red' ? 'Red' : 'Blue'} seat is already claimed.`);
        return null;
      }
      if (responseClaim.status === 410) {
        setMessage('This match was reset. A new player can now take this seat.');
        return null;
      }
      if (!responseClaim.ok) throw new Error('claim unavailable');
      const result = await responseClaim.json() as ClaimResponse;
      claimStorage.setItem(key, JSON.stringify({
        generation: result.generation,
        token: result.session.token,
        matchId: result.session.matchId,
      }));
      return result;
    };
    inFlight.current ??= run();
    void inFlight.current.then((result) => {
      if (!live) return;
      if (result !== null) {
        setClaim(result);
        return;
      }
      scheduleRetry();
    }).catch(() => {
      if (live) {
        setMessage('Waiting for the server…');
        scheduleRetry();
      }
    });
    return () => {
      live = false;
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
    };
  }, [attempt, claimStorage, fetchImpl, key, root, team]);

  const liveStore = useMemo(
    () => claim === null ? null : presentationSessionStore(claim.session, sessionStore),
    [claim, sessionStore],
  );

  useEffect(() => {
    if (claim === null) return undefined;
    const key = { sessionToken: claim.session.token, matchId: claim.session.matchId };
    if (wasReset) {
      clearMatchSession(key);
      return undefined;
    }
    return () => clearMatchSession(key);
  }, [claim, wasReset]);

  useEffect(() => {
    if (claim === null) return undefined;
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async (): Promise<void> => {
      try {
        const response = await fetchImpl(`${root}/presentation/status`);
        if (!response.ok) return;
        const status = await response.json() as PresentationStatus;
        if (live) setPalette(status.palette);
        if (live && (status.generation !== claim.generation || status.matchId !== claim.session.matchId)) {
          claimStorage.removeItem(key);
          setWasReset(true);
        }
      } finally {
        if (live) timer = setTimeout(() => void poll(), 1_000);
      }
    };
    timer = setTimeout(() => void poll(), 1_000);
    return () => { live = false; if (timer !== undefined) clearTimeout(timer); };
  }, [claim, claimStorage, fetchImpl, key, root]);

  return (
    <div data-testid="demo-player-root" data-team={team} style={{ width: '100%', height: '100%' }}>
      {wasReset ? (
        <main role="status" style={{ padding: 32 }}>
          <h1>Match reset</h1>
          <p>This match was reset. A new player can now take this seat.</p>
          <a href="/" style={ROUNDS_LINK}>Play Rounds</a>
        </main>
      ) : claim === null || liveStore === null ? (
        <main role="status" style={{ padding: 32 }}>
          <h1>{team === 'red' ? 'Red' : 'Blue'} Player</h1>
          <p>{message}</p>
        </main>
      ) : (
        <MemoryRouter initialEntries={['/colony']}>
          <SessionController
            transportFactory={transportFactory}
            lobby={{ baseUrl: root, fetchImpl }}
            sessionStore={liveStore}
            scheduler={scheduler}
            rng={rng}
            createRenderer={createRenderer}
            pvp
            presentationRoundsHandoff
            handshakeTimeoutMs={30 * 60 * 1_000}
          />
        </MemoryRouter>
      )}
    </div>
  );
}

const ROUNDS_LINK: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  minHeight: 'var(--hit-target)', padding: '0 20px', borderRadius: 'var(--r-pill)',
  background: 'var(--accent-strong)', color: 'var(--surface-card)',
  fontFamily: 'var(--font-display)', fontWeight: 700,
};
